import clsx from 'clsx'
import {FC, ReactNode, useEffect, useRef, useState} from 'react'
import {StepperComponent} from '../../assets/ts/components'
import {KTIcon} from './KTIcon'
import {toPersianDigits} from './datepicker/persianDateValue'

export interface IStepperStep {
  icon: ReactNode
  name: ReactNode
  content: ReactNode
  description?: ReactNode
  validate: boolean
  btn?: ReactNode
  backBtn?: ReactNode
  btnFn?: () => Promise<void>
  disabled?: boolean
}

type props = {
  steps: IStepperStep[]
  clickable?: boolean
  validate: boolean
  indicator?: boolean
  save?: () => any
  saveBtn?: ReactNode
  indicatorClass?: string
  externalStep?: number
  showPrevious?: boolean
}

const Stepper: FC<props> = ({
  steps,
  clickable = true,
  validate,
  save,
  saveBtn,
  indicatorClass,
  indicator = true,
  externalStep,
  showPrevious = true,
}) => {
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)
  const [btnKey, setBtnKey] = useState<number>(1)
  const headRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {}, [validate])

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(stepperRef.current as HTMLDivElement)
  }
  useEffect(() => {
    if (!stepperRef.current) {
      return
    }
    loadStepper()
  }, [stepperRef])

  useEffect(() => {
    setBtnKey(btnKey + 1)
    loadStepper()
  }, [stepper])

  const prevStep = () => {
    if (!stepper.current) {
      return
    }

    stepper.current.goPrev()
    setBtnKey(btnKey + 1)
    if (headRef != null && headRef.current != null) {
      headRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }
  const nextStep = async () => {
    if (!stepper.current) {
      return
    }

    if (stepper.current.currentStepIndex !== stepper.current.totalStepsNumber) {
      stepper.current.goNext()
      setBtnKey(btnKey + 1)
      if (headRef != null && headRef.current != null) {
        headRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    } else {
    }
  }
  const setStep = async (index: number) => {
    if (!stepper.current) {
      return
    }
    stepper.current.goto(index)
    setBtnKey(btnKey + 1)
  }

  useEffect(() => {
    if (externalStep) {
      setStep(externalStep)
    }
  }, [externalStep])

  useEffect(() => {
    setBtnKey(btnKey + 1)
  }, [saveBtn])

  function getNextButton() {
    if (
      steps.filter((x) => x.disabled != true)[
        stepper.current ? stepper.current.currentStepIndex - 1 : 0
      ]
    ) {
      let btn = steps.filter((x) => x.disabled != true)[
        stepper.current ? stepper.current.currentStepIndex - 1 : 0
      ].btn
      const btnFn = steps.filter((x) => x.disabled != true)[
        stepper.current ? stepper.current.currentStepIndex - 1 : 0
      ].btnFn
      if (!btn) {
        btn = (
          <>
            <button
              disabled={
                !(
                  clickable ||
                  steps.filter((x) => x.disabled != true)[
                    stepper.current ? stepper.current.currentStepIndex - 1 : 0
                  ].validate
                )
              }
              className='btn btn-lg btn-primary me-3'
              type='button'
            >
              <span className='indicator-label'>
                بعدی
                <KTIcon iconName='arrow-left' className='fs-3 ms-2 me-0 mt-2' />
              </span>
            </button>
          </>
        )
      }
      btn =
        clickable ||
        steps.filter((x) => x.disabled != true)[
          stepper.current ? stepper.current.currentStepIndex - 1 : 0
        ].validate ? (
          <span
            onClick={async () => {
              if (btnFn) await btnFn()
              nextStep()
            }}
          >
            {btn}
          </span>
        ) : (
          btn
        )
      if (
        stepper.current &&
        (stepper.current.totalStepsNumber == 0 ||
          stepper.current.totalStepsNumber == stepper.current.currentStepIndex)
      ) {
        btn = saveBtn ? (
          saveBtn
        ) : (
          <button className='btn btn-lg btn-success me-3' disabled={!validate} onClick={save}>
            <span className='indicator-label'>
              <i className='las la-save me-2 fs-2'></i>
              ذخیره
            </span>
          </button>
        )
      }
      return btn
    } else {
      return <></>
    }
  }

  const getPrvButton = () => {
    if (!showPrevious) return null

    if (
      steps.filter((x) => x.disabled != true)[
        stepper.current ? stepper.current.currentStepIndex - 1 : 0
      ]
    ) {
      const stepBackBtn = steps.filter((x) => x.disabled != true)[
        stepper.current ? stepper.current.currentStepIndex - 1 : 0
      ].backBtn
      return (
        <>
          {stepBackBtn != undefined ? (
            stepBackBtn
          ) : (stepper.current?.currentStepIndex || 1) <= 1 ? null : (
            <div className='mr-2'>
              <button
                onClick={prevStep}
                type='button'
                className='btn btn-lg btn-light-primary me-3'
                data-kt-stepper-action='previous'
              >
                <KTIcon iconName='arrow-right' className='fs-4 me-1' />
                قبل
              </button>
            </div>
          )}
        </>
      )
    } else {
      return <></>
    }
  }

  const isLastStep =
    !!stepper.current &&
    (stepper.current.totalStepsNumber == 0 ||
      stepper.current.totalStepsNumber == stepper.current.currentStepIndex)

  /**
   * Why «ذخیره» is disabled (509): on the last step only its greyed button showed, the step at fault was marked far
   * above. One line names the first step whose `validate` is false and goes to it; a caller's own `saveBtn` keeps its
   * own rules, and nothing is said while no step is at fault (the form is busy saving).
   */
  const getBlockedReason = () => {
    if (validate || saveBtn || !isLastStep) return null

    const blockingIndex = steps.findIndex((step) => step.disabled != true && !step.validate)
    if (blockingIndex < 0) return null

    const blockingStep = steps[blockingIndex]
    const isCurrentStep = blockingIndex + 1 == stepper.current?.currentStepIndex
    const stepNumber = toPersianDigits(
      typeof blockingStep.icon === 'string' || typeof blockingStep.icon === 'number'
        ? blockingStep.icon
        : blockingIndex + 1
    )

    return (
      <div
        className='mp-stepper-blocked d-flex align-items-center flex-wrap gap-2 w-100 rounded bg-light-warning text-gray-800 fs-7 fw-semibold px-3 py-2'
        style={{gridColumn: '1 / -1'}}
        role='status'
      >
        <i className='las la-exclamation-triangle text-warning fs-2' aria-hidden='true'></i>
        <span className='flex-grow-1'>
          {isCurrentStep ? (
            <>برای ذخیره، خطاهای همین مرحله را برطرف کنید.</>
          ) : (
            <>
              برای ذخیره، خطای مرحلهٔ {stepNumber} ({blockingStep.name}) را برطرف کنید.
            </>
          )}
        </span>
        {!isCurrentStep && (
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={() => {
              setStep(blockingIndex + 1)
              headRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
            }}
          >
            رفتن به مرحلهٔ {stepNumber}
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      {/* begin::Scroll */}
      <div
        ref={headRef}
        className='mp-stepper-viewport'
        // className='scroll-y p-1'
        // id='kt_modal_add_invoice_scroll'
        // data-kt-scroll='true'
        // data-kt-scroll-activate='{default: false, lg: true}'
        // data-kt-scroll-max-height='auto'
        // data-kt-scroll-dependencies='#kt_modal_add_invoice_header'
        // data-kt-scroll-wrappers='#kt_modal_add_invoice_scroll'
        // data-kt-scroll-offset='300px'
        style={{minHeight: '75vh'}}
      >
        <div
          ref={stepperRef}
          className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid mp-stepper-layout'
          id='kt_create_account_stepper'
        >
          {/* begin::Aside*/}
          {indicator && (
            <div
              className={clsx(
                'card d-flex justify-content-center justify-content-xl-start flex-row-auto p-5 me-3 mp-stepper-sidebar',
                indicatorClass
              )}
            >
              {/* begin::Wrapper*/}
              <div className='card-body px-1 py-2 pe-10 mp-stepper-sidebar__body'>
                {/* begin::Nav*/}
                <div className='stepper-nav mp-stepper-nav'>
                  {/* begin::Step*/}
                  {steps.map((step, ind) => {
                    return (
                      <div
                        key={`step-${ind}`}
                        className={clsx('stepper-item', {current: ind == 0})}
                        data-kt-stepper-element='nav'
                        onClick={() => {
                          clickable && setStep(ind + 1)
                        }}
                        style={{cursor: `${clickable ? 'pointer' : ''}`}}
                      >
                        {!step.disabled && (
                          <>
                            {/* begin::Wrapper*/}
                            <div className='stepper-wrapper'>
                              {/* begin::Icon*/}
                              <div className='stepper-icon w-40px h-40px'>
                                {step.validate ? (
                                  <i className='stepper-check fas fa-check text-success fs-2x'></i>
                                ) : (
                                  <i
                                    className='stepper-check las la-exclamation-triangle fs-2x text-danger'
                                    title='اطلاعات ناقص'
                                  ></i>
                                )}
                                <span className='stepper-number'>{step.icon}</span>
                              </div>
                              {/* end::Icon*/}

                              {/* begin::Label*/}
                              <div className='stepper-label'>
                                <h3 className='stepper-title'>{step.name}</h3>
                                {step.description && (
                                  <div className='stepper-desc fw-semibold'>{step.description}</div>
                                )}
                              </div>
                              {/* end::Label*/}
                            </div>
                            {/* end::Wrapper*/}

                            {/* begin::Line*/}
                            <div className='stepper-line h-40px'></div>
                            {/* end::Line*/}
                          </>
                        )}

                        {step.disabled && <></>}
                      </div>
                    )
                  })}
                  {/* end::Step*/}
                </div>
                {/* end::Nav*/}
              </div>
              {/* end::Wrapper*/}
            </div>
          )}
          {/* begin::Aside*/}
          <div className='d-flex flex-grow-1 w-100 flex-row-fluid flex-center bg-body rounded mp-stepper-content-shell'>
            <div className='py-5 w-100 px-2 mp-stepper-form' id='kt_create_account_form'>
              {/* {steps
                .filter((x) => x.disabled == undefined || x.disabled == false)
                .map((step, ind) => {
                  return (
                    <div
                      key={`content-${ind}`}
                      className={clsx({current: ind == 0})}
                      data-kt-stepper-element='content'
                    >
                      <div className='d-flex flex-column w-100'>{step.content}</div>
                    </div>
                  )
                })} */}
              <div
                key={`content-${stepper.current?.currentStepIndex || 0}`}
                className={clsx('current')}
                data-kt-stepper-element='content'
              >
                <div
                  className='d-flex flex-column w-100 mp-stepper-content'
                  style={{minHeight: '60vh'}}
                >
                  {steps.filter((x) => x.disabled != true)[
                    stepper.current ? stepper.current.currentStepIndex - 1 : 0
                  ] &&
                    steps.filter((x) => x.disabled != true)[
                      stepper.current ? stepper.current.currentStepIndex - 1 : 0
                    ].content}
                </div>
              </div>
              <div className='d-flex flex-stack flex-wrap pt-10 mp-stepper-actions' key={btnKey}>
                {getBlockedReason()}
                <div>{getPrvButton()}</div>
                <div>{getNextButton()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* end::Scroll */}
    </>
  )
}

export {Stepper}
